/**
 * Action handlers for Calculator
 */
import { useCallback } from 'react';
import type { Dispatch } from 'react';
import { useAnalytics } from '../../../shared/hooks';
import { useShareCalculation } from '../../shared-calculation/hooks/useShareCalculation';
import { useGetHourlyData } from '../../../shared/hooks';
import { saveCalculationForCurrentYear } from '../../../lib/api/savedCalculationApi';
import { calculateAnnualComparison, type ConsumptionPattern } from '../../../../../amplify/data/utils/tariff-calculator';
import { buildOSDPricing } from '../../../../../amplify/data/utils/pricing-config';
import { getAvailableTariffsForOSD, type OSDCode as OSDMetaCode } from '../../../utils/osdMetadata';
import type { OSDCode } from '../../../types/osd.types';
import type { CalculatorFormValues, CalculationResults, TariffType, CalculatorAction } from '../types/calculator.types';

interface UseCalculatorActionsProps {
  dispatch: Dispatch<CalculatorAction>;
  selectedOSD: OSDCode;
  calculationYear: number;
  initialSharedData: CalculatorFormValues | null;
  resultsRef: React.RefObject<HTMLDivElement | null>;
}

export function useCalculatorActions(props: UseCalculatorActionsProps) {
  const { dispatch, selectedOSD, calculationYear, initialSharedData, resultsRef } = props;
  const analytics = useAnalytics();
  const { shareCalculation } = useShareCalculation();
  const { getHourlyData } = useGetHourlyData();

  const convertHourlyDataToPattern = useCallback(
    (hourlyData: Array<any>, _year: number, _month: number) => {
      const weekdayHours = new Array(24).fill(0);
      const weekendHours = new Array(24).fill(0);
      const weekdayCounts = new Array(24).fill(0);
      const weekendCounts = new Array(24).fill(0);

      hourlyData.forEach((point) => {
        const date = new Date(point.timestamp);
        const dayOfWeek = date.getDay();
        const hour = date.getHours();
        const consumption = point.consumption;

        if (dayOfWeek === 0 || dayOfWeek === 6) {
          weekendHours[hour] += consumption;
          weekendCounts[hour] += 1;
        } else {
          weekdayHours[hour] += consumption;
          weekdayCounts[hour] += 1;
        }
      });

      const avgWeekdayHours = weekdayHours.map((total, hour) =>
        weekdayCounts[hour] > 0 ? total / weekdayCounts[hour] : 0
      );
      const avgWeekendHours = weekendHours.map((total, hour) =>
        weekendCounts[hour] > 0 ? total / weekendCounts[hour] : 0
      );

      const weekdaySum = avgWeekdayHours.reduce((sum, val) => sum + val, 0);
      const weekendSum = avgWeekendHours.reduce((sum, val) => sum + val, 0);

      return {
        weekday: weekdaySum > 0 ? avgWeekdayHours.map(v => v / weekdaySum) : Array(24).fill(1 / 24),
        weekend: weekendSum > 0 ? avgWeekendHours.map(v => v / weekendSum) : Array(24).fill(1 / 24),
      };
    },
    []
  );

  const transformBackendResults = useCallback((annualResults: any, osd: OSDMetaCode): CalculationResults => {
    const availableTariffs = getAvailableTariffsForOSD(osd);
    const isTariffAvailable = (code: string) => availableTariffs.some(t => t.toLowerCase() === code.toLowerCase());
    const transformTariff = (r: any) => ({
      tariffName: '', energyCost: r.totalProductionCost * 1.23, distributionCost: r.totalDistributionCost * 1.23,
      totalAnnualCost: r.totalCost, savings: 0, averageCostPerKwh: r.averageCostPerKwh, isRecommended: false,
    });
    const availableCosts: any[] = [];
    if (isTariffAvailable('G11')) availableCosts.push({ code: 'G11', cost: annualResults.g11.totalCost });
    if (isTariffAvailable('G12')) availableCosts.push({ code: 'G12', cost: annualResults.g12.totalCost });
    if (isTariffAvailable('G12w') && annualResults.g12w) availableCosts.push({ code: 'G12w', cost: annualResults.g12w.totalCost });
    if (isTariffAvailable('G12as') && annualResults.g12as) availableCosts.push({ code: 'G12as', cost: annualResults.g12as.totalCost });
    if (isTariffAvailable('G13') && annualResults.g13) availableCosts.push({ code: 'G13', cost: annualResults.g13.totalCost });
    if (isTariffAvailable('G13s') && annualResults.g13s) availableCosts.push({ code: 'G13s', cost: annualResults.g13s.totalCost });
    if (isTariffAvailable('G12n') && annualResults.g12n) availableCosts.push({ code: 'G12n', cost: annualResults.g12n.totalCost });
    if (isTariffAvailable('G12r') && annualResults.g12r) availableCosts.push({ code: 'G12r', cost: annualResults.g12r.totalCost });
    const cheapestCost = availableCosts.length > 0 ? Math.min(...availableCosts.map(t => t.cost)) : annualResults.g11.totalCost;
    const recommendedTariff = availableCosts.length > 0 ? availableCosts.reduce((min, c) => c.cost < min.cost ? c : min).code : 'G11';
    const maxCost = availableCosts.length > 0 ? Math.max(...availableCosts.map(t => t.cost)) : annualResults.g11.totalCost;
    const potentialSavings = maxCost - cheapestCost;
    const results: any = {};
    if (isTariffAvailable('G11')) { const g11 = transformTariff(annualResults.g11); g11.tariffName = 'G11'; g11.savings = cheapestCost - annualResults.g11.totalCost; g11.isRecommended = recommendedTariff === 'G11'; results.g11 = g11; }
    if (isTariffAvailable('G12')) { const g12 = transformTariff(annualResults.g12); g12.tariffName = 'G12'; g12.savings = cheapestCost - annualResults.g12.totalCost; g12.isRecommended = recommendedTariff === 'G12'; results.g12 = g12; }
    if (isTariffAvailable('G12w') && annualResults.g12w) { const g12w = transformTariff(annualResults.g12w); g12w.tariffName = 'G12w'; g12w.savings = cheapestCost - annualResults.g12w.totalCost; g12w.isRecommended = recommendedTariff === 'G12w'; results.g12w = g12w; }
    if (isTariffAvailable('G12as') && annualResults.g12as) { const g12as = transformTariff(annualResults.g12as); g12as.tariffName = 'G12as'; g12as.savings = cheapestCost - annualResults.g12as.totalCost; g12as.isRecommended = recommendedTariff === 'G12as'; results.g12as = g12as; }
    if (isTariffAvailable('G13') && annualResults.g13) { const g13 = transformTariff(annualResults.g13); g13.tariffName = 'G13'; g13.savings = cheapestCost - annualResults.g13.totalCost; g13.isRecommended = recommendedTariff === 'G13'; results.g13 = g13; }
    if (isTariffAvailable('G13s') && annualResults.g13s) { const g13s = transformTariff(annualResults.g13s); g13s.tariffName = 'G13s'; g13s.savings = cheapestCost - annualResults.g13s.totalCost; g13s.isRecommended = recommendedTariff === 'G13s'; results.g13s = g13s; }
    if (isTariffAvailable('G12n') && annualResults.g12n) { const g12n = transformTariff(annualResults.g12n); g12n.tariffName = 'G12n'; g12n.savings = cheapestCost - annualResults.g12n.totalCost; g12n.isRecommended = recommendedTariff === 'G12n'; results.g12n = g12n; }
    if (isTariffAvailable('G12r') && annualResults.g12r) { const g12r = transformTariff(annualResults.g12r); g12r.tariffName = 'G12r'; g12r.savings = cheapestCost - annualResults.g12r.totalCost; g12r.isRecommended = recommendedTariff === 'G12r'; results.g12r = g12r; }
    results.recommendedTariff = recommendedTariff;
    results.potentialSavings = potentialSavings;
    results.savingsPercent = maxCost > 0 ? (potentialSavings / maxCost) * 100 : 0;
    return results as CalculationResults;
  }, []);

  const handleCalculate = useCallback(async (values: CalculatorFormValues) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'RESET_SAVE_STATE' });
    dispatch({ type: 'SET_CURRENT_TARIFF', payload: values.currentTariff });
    dispatch({ type: 'SET_BASELINE_TARIFF', payload: values.currentTariff });
    dispatch({ type: 'SET_FORM_VALUES', payload: values });
    try {
      await new Promise(r => setTimeout(r, 500));
      let pattern: ConsumptionPattern = { weekday: values.hourlyConfig?.weekday || Array(24).fill(1 / 24), weekend: values.hourlyConfig?.weekend || Array(24).fill(1 / 24) };
      if (values.consumptionPattern === 'FROM_CSV_IMPORT') {
        try {
          const year = new Date().getFullYear();
          const allMonthsData: any[] = [];
          for (let month = 1; month <= 12; month++) {
            const yearMonth = year + '-' + String(month).padStart(2, '0');
            try {
              const result = await getHourlyData(yearMonth);
              if (!result.hourlyData || result.hourlyData.length === 0) continue;
              allMonthsData.push(convertHourlyDataToPattern(result.hourlyData, year, month));
            } catch (e) { continue; }
          }
          if (allMonthsData.length > 0) {
            const avgWeekday = Array(24).fill(0);
            allMonthsData.forEach(m => m.weekday.forEach((v: number, h: number) => avgWeekday[h] += v));
            avgWeekday.forEach((_, h) => avgWeekday[h] /= allMonthsData.length);
            const avgWeekend = Array(24).fill(0);
            allMonthsData.forEach(m => m.weekend.forEach((v: number, h: number) => avgWeekend[h] += v));
            avgWeekend.forEach((_, h) => avgWeekend[h] /= allMonthsData.length);
            pattern = { weekday: avgWeekday, weekend: avgWeekend };
          }
        } catch {
          // Silently ignore pattern parsing errors - fall back to default pattern
        }
      }
      const osdPricing = buildOSDPricing(selectedOSD, calculationYear);
      const annualResults = calculateAnnualComparison(values.monthlyConsumption || Array(12).fill(292), osdPricing, pattern, calculationYear);
      const results = transformBackendResults(annualResults, selectedOSD as OSDMetaCode);
      dispatch({ type: 'SET_RESULTS', payload: results });
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      analytics.trackCalculatorCompleted({ current_tariff: values.currentTariff, num_tariffs_compared: 6, potential_savings: results.potentialSavings });
      analytics.trackResultsViewed({ tariff_count: 6, has_savings: results.potentialSavings > 0 });
      const dataChanged = !initialSharedData || JSON.stringify(values) !== JSON.stringify(initialSharedData);
      if (dataChanged) {
        try {
          const valuesWithOSD = { ...values, selectedOSD };
          const shareResult = await shareCalculation(valuesWithOSD, calculationYear);
          dispatch({ type: 'SET_SHARE_URL', payload: shareResult.shareUrl });
          dispatch({ type: 'SET_SHARE_HASH', payload: shareResult.calculationHash });
          window.history.replaceState(null, '', '/calc/' + calculationYear + '/' + selectedOSD + '/' + shareResult.calculationHash);
        } catch {
          // Silently ignore share URL creation errors - calculation still works
        }
      }
    } catch (e) {
      dispatch({ type: 'SET_ERROR', payload: 'Wystąpił błąd podczas obliczania. Spróbuj ponownie.' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, selectedOSD, calculationYear, initialSharedData, analytics, shareCalculation, getHourlyData, convertHourlyDataToPattern, transformBackendResults, resultsRef]);

  const handleBaselineTariffChange = useCallback((tariff: TariffType, oldBaseline: TariffType) => {
    dispatch({ type: 'SET_BASELINE_TARIFF', payload: tariff });
    analytics.trackBaselineChanged(oldBaseline, tariff);
  }, [dispatch, analytics]);

  const handleSaveCalculation = useCallback(async (formValues: CalculatorFormValues, results: CalculationResults) => {
    dispatch({ type: 'SET_SAVE_STATUS', payload: 'saving' });
    dispatch({ type: 'SET_SAVE_ERROR', payload: undefined });
    try {
      if (!formValues || !results) throw new Error('Brak danych do zapisania');
      const calculationInput = {
        osd: selectedOSD, // REQUIRED: Include OSD for Multi-OSD support
        monthlyConsumption: formValues.monthlyConsumption,
        householdSize: typeof formValues.householdSize === 'number' ? formValues.householdSize : 5,
        hasHeatPump: formValues.hasHeatPump || false,
        hasSolarPanels: formValues.hasSolar || false,
        hasElectricVehicle: formValues.hasEV || false,
        hourlyPattern: formValues.hourlyConfig || { weekday: [], weekend: [] },
        calculationResults: { tariffs: results, currentTariff: formValues.currentTariff, recommendedTariff: results.recommendedTariff, potentialSavings: results.potentialSavings, distributorId: selectedOSD, calculatedAt: new Date().toISOString() },
      };
      await saveCalculationForCurrentYear(calculationInput);
      dispatch({ type: 'SET_SAVE_STATUS', payload: 'success' });
      setTimeout(() => dispatch({ type: 'SET_SAVE_STATUS', payload: 'idle' }), 5000);
    } catch (err) {
      dispatch({ type: 'SET_SAVE_ERROR', payload: err instanceof Error ? err.message : 'Nie udało się zapisać kalkulacji. Spróbuj ponownie.' });
      dispatch({ type: 'SET_SAVE_STATUS', payload: 'error' });
    }
  }, [dispatch, selectedOSD]);

  return { handleCalculate, handleBaselineTariffChange, handleSaveCalculation };
}
